import * as braze from "@braze/web-sdk"
import { deferUntilPermitted, BRAZE_DIDOMI_VENDOR_ID, DIDOMI_ANALYTICS_PURPOSE_ID } from "../Didomi"
import { getTotalArticles } from "@theconversation/shared-components/article-read-tracking"
import { sendPendingEvents } from "./customEvents"
import { emailToHash } from "../email-hash"
import { brazeTrackingCookiePresent } from "./trackingCookie"
import { nonProdLogger } from "../nonProdLogger"
export { trackCustomEvent } from "./customEvents"
export { trackArticleViewed } from "./articleViewedEvent"
export { trackNewsletterSubscribed } from "./newsletterSubscribedEvent"
export { brazeTrackingCookiePresent, setBrazeTrackingCookie } from "./trackingCookie"
export { trackNewsletterLandingPageViewed } from "./newsletterLandingPageViewedEvent"

/**
 * Boot the Braze Web SDK.
 *
 * In order to avoid blowing through our limited MAU quota, this should only be called
 * when one or more of the following conditions is true:
 * - User has just read their second article
 * - User has just subscribed to a newsletter
 * - User has just made a donation (or started the process to make a donation)
 * - User has previously been activated for Braze
 *
 * And additionally, in all cases, the user must have either given GDPR tracking consent,
 * or else be in a region where consent is not required.
 *
 * Remember to confirm you have Didomi consent for these:
 * - BRAZE_DIDOMI_VENDOR_ID
 * - DIDOMI_ANALYTICS_PURPOSE_ID
 */
export function brazeInitialise(
  sdk_endpoint: string,
  api_key: string,
  userIdentifier: string | null = null,
) {
  // If Braze is already in session, there's no need to re-initialise it.
  if (braze.isInitialized()) {
    return
  }

  deferUntilPermitted(BRAZE_DIDOMI_VENDOR_ID, DIDOMI_ANALYTICS_PURPOSE_ID, () => {
    braze.initialize(api_key, {
      baseUrl: sdk_endpoint,
      enableLogging: !isOnProd,
      allowUserSuppliedJavascript: false, // set to `true` to support custom HTML messages
    })

    if (userIdentifier) {
      braze.changeUser(userIdentifier)
    }

    braze.openSession()

    sendPendingEvents()
  })
}

/**
 * Given a user's email address, identify them within Braze.
 *
 * Requires that brazeInitialise() has been called already.
 *
 * The error catching is there because our Cypress CI tests dont have access to the
 * browser cryptography functions needed to generate an email hash, but also it doesn't
 * matter. And it makes sense to avoid blowing up a Reader's browser too, if they're on
 * something old or unsupported.
 *
 * @returns A promise which when resolves indicates that the user change has been actioned.
 */
export function identifyBrazeUser(email: string): Promise<void> {
  const promise = new Promise<void>((resolve, reject) => {
    deferUntilPermitted(BRAZE_DIDOMI_VENDOR_ID, DIDOMI_ANALYTICS_PURPOSE_ID, () => {
      emailToHash(email)
        .then((emailHash) => {
          if (emailHash == null) {
            const message = "Empty or invalid email provided to identifyBrazeUser"
            nonProdLogger(message)
            reject(new Error(message))
          } else {
            braze.changeUser(emailHash)
            resolve()
          }
        })
        .catch((reason) => {
          const message =
            reason instanceof Error
              ? `Could not set external id for Braze user: ${reason.message}`
              : "Could not set external id for Braze user"
          nonProdLogger(message)
          reject(new Error(message))
        })
    })
  })
  return promise
}

// Thought it was nice to enable Braze's logging in dev/staging, at least initially,
// and so built this quick helper to assist with that since we don't have
// access to the regular Nextjs or Rails config in shared components.
function isOnProd() {
  return window.location.host.includes("theconversation.com")
}

/**
 * This function checks if the business rules have been met such that we should track
 * this user with Braze's analytics. The conditions are meant to be as follows, but
 * these are not currently handled fully or correctly:
 * - User has read at least 2 articles in the last 30 days (currently just implemented as 2 articles for all time)
 * - User is a newsletter subscriber (not yet implemented)
 * - User is a financial donor (how recently?) (not yet implemented)
 *
 */
export async function conditionsMetToTrackUser() {
  if (getTotalArticles() >= 2) {
    return true
  }

  if (await brazeTrackingCookiePresent()) {
    return true
  }

  // Is this too fragile a method to tell the user is on the newsletter landing page?
  if (/^\/?[a-zA-Z-]+\/\bnewsletters\b/.test(window.location.pathname)) {
    return true
  }

  return false
}
